import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { SUBS_BY_SECTION, sectionLabel, slugify } from "@/lib/categories";
import { groupVariants } from "@/lib/variants";
import ProductCard from "@/components/ProductCard";

const sections = [
  { key: "all", label: "All" },
  { key: "fashion", label: "Fashion Wear" },
  { key: "furnishing", label: "Home Furnishing" },
  { key: "mumkey", label: "Mumkey" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "all";
  const sub = params.get("sub") || "";
  const [products, setProducts] = useState(null);
  const [covers, setCovers] = useState({});

  useEffect(() => {
    api.get("/tile-covers").then((r) => setCovers(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    setProducts(null);
    const query = {};
    if (section !== "all") query.category = section;
    if (sub) query.subcategory = sub;
    api
      .get("/products", { params: query })
      .then((r) => setProducts(r.data))
      .catch(() => setProducts([]));
  }, [section, sub]);

  const setSection = (s) => {
    const next = {};
    if (s !== "all") next.section = s;
    setParams(next);
  };

  const groupedMode = section !== "all" && !sub;
  const groups = (() => {
    if (!groupedMode || !products) return [];
    const subs = SUBS_BY_SECTION[section] || [];
    const list = subs
      .map((name) => ({ name, items: products.filter((p) => p.subcategory === name) }))
      .filter((g) => g.items.length > 0);
    const leftovers = products.filter((p) => !subs.includes(p.subcategory));
    if (leftovers.length > 0) list.push({ name: "More Styles", items: leftovers });
    return list;
  })();

  return (
    <div data-testid="shop-page" className="px-6 py-16 md:px-12 lg:px-20 lg:py-24">
      <div className="mx-auto max-w-[1400px]">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-soot">The Collections</p>
          <h1 className="mt-4 font-serif text-5xl font-light tracking-tight sm:text-6xl md:text-7xl">
            {sub ? (
              <span className="italic">{sub}</span>
            ) : section !== "all" ? (
              <span className="italic">{sectionLabel(section)}</span>
            ) : (
              <>Shop <span className="italic">Hunar Designs</span></>
            )}
          </h1>
          {sub && (
            <p className="mt-3 text-sm text-soot" data-testid="shop-breadcrumb">
              {sectionLabel(section)} / {sub}
            </p>
          )}
        </motion.div>

        <div className="mt-10 flex flex-wrap gap-3" data-testid="section-filters">
          {sections.map((s) => (
            <button
              key={s.key}
              data-testid={`section-tab-${s.key}`}
              onClick={() => setSection(s.key)}
              className={`rounded-full border px-6 py-2.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 ${
                section === s.key && !sub
                  ? "border-obsidian bg-obsidian text-bone"
                  : "border-seam text-soot hover:border-obsidian hover:text-obsidian"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {sub && (
          <Link
            to={`/shop?section=${section}`}
            data-testid="back-to-collections"
            className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-soot transition-colors hover:text-obsidian"
          >
            <ArrowLeft className="h-4 w-4" /> Back to all {sectionLabel(section)}
          </Link>
        )}

        {products === null ? (
          <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse bg-linen" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="mt-20 font-serif text-2xl italic text-soot" data-testid="shop-empty">
            Nothing here yet — new pieces are on their way.
          </p>
        ) : groupedMode ? (
          <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 md:grid-cols-3 lg:grid-cols-4" data-testid="subcategory-tiles">
            {groups.map((g, gi) => {
              const cover = covers[`${section}|${g.name}`] || g.items[0]?.images?.[0];
              return (
                <motion.div
                  key={g.name}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: gi * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={`/shop?section=${section}&sub=${encodeURIComponent(g.name)}`}
                    data-testid={`subcategory-tile-${slugify(g.name)}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden bg-linen">
                      {cover && (
                        <img
                          src={cover}
                          alt={g.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                        />
                      )}
                      <div className="absolute inset-0 bg-obsidian/0 transition-colors duration-500 group-hover:bg-obsidian/15" />
                    </div>
                    <div className="mt-4 flex items-baseline justify-between gap-3">
                      <h2 className="font-serif text-xl italic leading-tight sm:text-2xl">{g.name}</h2>
                      <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.12em] text-soot">
                        {g.items.length} {g.items.length === 1 ? "design" : "designs"}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className="mt-16 grid grid-cols-2 gap-x-6 gap-y-14 lg:grid-cols-3" data-testid="product-grid">
            {groupVariants(products).map(({ product: p, colorCount }, i) => (
              <div key={p.id} className={i % 2 === 1 ? "lg:translate-y-12" : ""}>
                <ProductCard product={p} index={i} colorCount={colorCount} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
